/**
 * Applicazione Turni - UOC Anestesia & UOS Medicina del Dolore
 * Piattaforma collaborativa
 */

/* store: un solo modulo condiviso (in dev via ESM; in prod Vite raggruppa in un bundle). */
import '../styles/main.css';
import { getState, subscribe, initStore, isBackendConnected, canEditDoctors, canManageUsers, setAbsencesCalendarWeekRange } from './store.js';
import {
  getApiStatus,
  login,
  getStoredUser,
  clearAuth,
  getAuthToken,
  setAuth,
  updateStoredUser,
  apiLogout,
  apiChangeMyPassword,
  apiGetMe,
  apiPatchMe,
  apiForgotPassword,
  apiResetPassword,
  apiGetBoardFeed,
  apiListShiftChangeRequests,
  apiGetBoardModerationQueue,
  apiGetPushVapidPublicKey,
  apiPushSubscribe,
  apiNotificationsUnreadCount,
} from './api.js';
import { openNotificationsPanel } from './components/NotificationsPanel.js';
import {
  renderEmailRecipientsEditor,
  collectEmailRecipients,
  validateEmailRecipients,
} from './components/EmailRecipientsEditor.js';
import { buildSlotsFromCalendar } from './shiftGenerator.js';
import { getMonthYearFromStr, getNextCalendarMonthBounds, getIsoWeekGridSpanForCalendarMonth, getMondayOfWeek, isDoctorCeased } from './utils.js';
import { renderDoctorProfile } from './components/DoctorProfile.js';
import { renderAbsencesCalendar } from './components/AbsencesCalendar.js';
import { renderShiftPlanner } from './components/ShiftPlanner_v2.js';
import { renderCalendarPlanner } from './components/CalendarPlanner.js';
import { renderWorkloadView } from './components/WorkloadView.js';
import { renderUsersView } from './components/UsersView.js';
import { renderManualView } from './components/ManualView.js';
import { renderDoctorKioskView, disposeDoctorKioskView } from './components/DoctorKioskView.js';
import { renderBoardView } from './components/BoardView.js';
import { initLayoutHints } from './device.js';

/**
 * Fallback se /api/status non è ancora disponibile (offline o prima della prima risposta).
 * In produzione la stringa mostrata viene da buildVersion (server + dist/build-info.json).
 * Aggiorna con update-version.sh.
 */
const BUILD_VERSION_FALLBACK = 'v. 20261004-155541 — 04/10/2026 15:55:41';

function applyBuildMetadataFromStatus(st) {
  if (!st || typeof st !== 'object') return;
  const display = typeof st.buildVersion === 'string' ? st.buildVersion.trim() : '';
  const token = typeof st.buildVersionToken === 'string' ? st.buildVersionToken.trim() : '';
  if (display) window.__TURNI_DISPLAY_BUILD = display;
  if (token) window.__TURNI_BUILD_TOKEN = token;
}

function applySolverTimeoutFromStatus(st) {
  if (!st || typeof st !== 'object') return;
  const sec = Number(st.solverProxyTimeoutSec);
  if (sec > 0) {
    window.TURNI_SOLVER_PROXY_TIMEOUT_MS = sec * 1000;
    window.TURNI_SOLVER_TIMEOUT_MS = sec * 1000;
  } else if (Number(st.solverClientTimeoutMsInfra) > 0) {
    window.TURNI_SOLVER_PROXY_TIMEOUT_MS = Number(st.solverClientTimeoutMsInfra);
    window.TURNI_SOLVER_TIMEOUT_MS = Number(st.solverClientTimeoutMsInfra);
  }
}

function getAppVersion() {
  if (typeof window !== 'undefined' && window.__TURNI_DISPLAY_BUILD) return window.__TURNI_DISPLAY_BUILD;
  return BUILD_VERSION_FALLBACK;
}

async function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  const isSecureContext =
    window.location.protocol === "https:" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  if (!isSecureContext) return null;
  try {
    const hadController = !!navigator.serviceWorker.controller;
    // URL stabile + updateViaCache:none: evita loop Safari (register con ?v= + update +
    // skipWaiting/claim → controllerchange → reload → ripeti).
    const registration = await navigator.serviceWorker.register("/sw.js", {
      updateViaCache: "none",
    });

    const SW_UPDATE_MIN_MS = 60 * 60 * 1000;
    let lastUpdateAt = 0;
    const recheck = () => {
      const now = Date.now();
      if (now - lastUpdateAt < SW_UPDATE_MIN_MS) return;
      lastUpdateAt = now;
      void registration.update();
    };
    // register() controlla già gli aggiornamenti: non forzare update() immediato.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") recheck();
    });
    window.addEventListener("pageshow", (ev) => {
      if (ev.persisted) recheck();
    });

    if (hadController) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        // sessionStorage sopravvive al reload nella stessa tab → al massimo un reload per sessione.
        try {
          if (sessionStorage.getItem("turni-sw-reload")) return;
          sessionStorage.setItem("turni-sw-reload", "1");
        } catch (_) {
          return;
        }
        window.location.reload();
      });
    }

    registration.addEventListener("updatefound", () => {
      const nextWorker = registration.installing;
      if (!nextWorker) return;
      nextWorker.addEventListener("statechange", () => {
        if (nextWorker.state === "installed" && navigator.serviceWorker.controller) {
          console.info("Nuova versione PWA: aggiornamento service worker in corso…");
        }
      });
    });
    if (!window.__turniSwMessageBound) {
      window.__turniSwMessageBound = true;
      navigator.serviceWorker.addEventListener("message", (ev) => {
        const d = ev?.data || {};
        if (d.type !== "turni-push") return;
        void refreshBoardBell(true);
        if (d.notificationId) {
          import("./components/NotificationsPanel.js")
            .then((m) =>
              m.openNotificationsPanel({
                highlightId: d.notificationId,
                onChanged: () => {
                  void refreshBoardBell(true);
                },
              }),
            )
            .catch(() => {});
        }
      });
    }
    return registration;
  } catch (err) {
    console.warn("Service worker non registrato:", err);
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function syncWebPushSubscription() {
  if (typeof window === "undefined") return;
  if (!("Notification" in window) || !("PushManager" in window)) return;
  const isSecureContext =
    window.location.protocol === "https:" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  if (!isSecureContext) return;
  try {
    const meta = await apiGetPushVapidPublicKey();
    if (!meta?.enabled || !meta?.publicKey) return;
    let perm = Notification.permission;
    if (perm === "denied") return;
    if (perm === "default") {
      perm = await Notification.requestPermission();
      if (perm !== "granted") return;
    }
    const reg = await navigator.serviceWorker.ready;
    const appKey = urlBase64ToUint8Array(meta.publicKey);
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appKey,
      });
    }
    await apiPushSubscribe(sub.toJSON());
  } catch (err) {
    console.warn("Web Push non attivato:", err);
  }
}

function mountNetworkStatusBanner() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (document.getElementById("network-status-banner")) return;
  const banner = document.createElement("div");
  banner.id = "network-status-banner";
  banner.style.cssText = [
    "position:fixed",
    "left:50%",
    "bottom:12px",
    "transform:translateX(-50%)",
    "padding:8px 12px",
    "border-radius:10px",
    "font:500 0.85rem/1.2 system-ui, sans-serif",
    "z-index:9999",
    "display:none",
    "box-shadow:0 6px 18px rgba(0,0,0,0.2)"
  ].join(";");
  document.body.appendChild(banner);

  const show = (text, bg) => {
    banner.textContent = text;
    banner.style.background = bg;
    banner.style.color = "#fff";
    banner.style.display = "block";
  };
  const hide = () => {
    banner.style.display = "none";
  };
  const onOffline = () => show("Sei offline: alcune funzioni potrebbero non essere disponibili.", "#b91c1c");
  const onOnline = () => {
    show("Connessione ripristinata.", "#166534");
    setTimeout(hide, 2000);
  };

  window.addEventListener("offline", onOffline);
  window.addEventListener("online", onOnline);
  if (!navigator.onLine) onOffline();
}

function getUserDisplayLabel(state) {
  const u = state?.currentUser;
  if (!u) return '';
  if (u.doctorId && state.doctors?.length) {
    const doc = state.doctors.find(d => d.id === u.doctorId);
    if (doc) return `${doc.cognome} ${doc.sigla}`;
  }
  return u.username || '';
}

/** Chiude menu mobile / backdrop (evita che resti sopra alle modali e blocchi tap sui campi). */
function closeHeaderNavIfOpen() {
  const app = document.getElementById('app');
  const header = app?.querySelector?.('.header');
  if (!header) return;
  header.classList.remove('header--nav-open');
  const backdrop = header.querySelector('#nav-backdrop');
  if (backdrop) backdrop.hidden = true;
  const toggle = header.querySelector('#nav-toggle');
  if (toggle) toggle.setAttribute('aria-expanded', 'false');
  document.body.style.overflow = '';
}

function bindHeaderNav(app) {
  const header = app.querySelector('.header');
  if (!header || header.dataset.navBound === '1') return;
  header.dataset.navBound = '1';
  const toggle = header.querySelector('#nav-toggle');
  const backdrop = header.querySelector('#nav-backdrop');
  const nav = header.querySelector('#main-nav');
  if (!toggle || !backdrop || !nav) return;

  const close = () => {
    header.classList.remove('header--nav-open');
    toggle.setAttribute('aria-expanded', 'false');
    backdrop.hidden = true;
    document.body.style.overflow = '';
  };
  const open = () => {
    header.classList.add('header--nav-open');
    toggle.setAttribute('aria-expanded', 'true');
    backdrop.hidden = false;
    document.body.style.overflow = 'hidden';
  };

  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    if (header.classList.contains('header--nav-open')) close();
    else open();
  });
  backdrop.addEventListener('click', close);
  nav.querySelectorAll('.nav-btn').forEach((btn) => {
    btn.addEventListener('click', () => { close(); });
  });
  const mq = window.matchMedia('(min-width: 901px)');
  mq.addEventListener('change', () => { if (mq.matches) close(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && header.classList.contains('header--nav-open')) close();
  });
}
const VIEWS = {
  kiosk: 'kiosk',
  calendario: 'calendario',
  medici: 'medici',
  assenze: 'assenze',
  turni: 'turni',
  carichi: 'carichi',
  bacheca: 'bacheca',
  utenti: 'utenti',
  manuale: 'manuale',
};

let currentView = VIEWS.turni;
let previousView = null;
let currentDoctorId = null;
let boardBellState = {
  unreadCount: 0,
  loading: false,
  lastFetchMs: 0,
  breakdown: {
    boardUnread: 0,
    pendingPostApprovals: 0,
    pendingPeerMine: 0,
    pendingDirectorQueue: 0,
    requesterUpdates: 0,
    inboxUnread: 0,
  },
};
let boardBellPollTimer = null;
const BOARD_BELL_POLL_MS = 120000;

const VIEW_LABELS = {
  [VIEWS.kiosk]: 'Home',
  [VIEWS.calendario]: 'Calendario',
  [VIEWS.medici]: 'Medici',
  [VIEWS.assenze]: 'Assenze',
  [VIEWS.turni]: 'Piano Turni',
  [VIEWS.carichi]: 'Carichi',
  [VIEWS.bacheca]: 'Bacheca',
  [VIEWS.utenti]: 'Utenti',
  [VIEWS.manuale]: 'Manuale',
};

const VIEW_META = {
  [VIEWS.kiosk]: { icon: '🩺' },
  [VIEWS.calendario]: { icon: '📅' },
  [VIEWS.medici]: { icon: '👨‍⚕️' },
  [VIEWS.assenze]: { icon: '🏖️' },
  [VIEWS.turni]: { icon: '🗓️' },
  [VIEWS.carichi]: { icon: '📊' },
  [VIEWS.bacheca]: { icon: '📣' },
  [VIEWS.utenti]: { icon: '👥' },
  [VIEWS.manuale]: { icon: '📘' },
};

function hasAssignedDoctorCode(user) {
  if (!user || typeof user !== 'object') return false;
  const doctorFields = [
    user.doctorId,
    user.doctor_id,
    user.doctorCode,
    user.doctor_code,
    user.codiceMedico,
    user.codice_medico,
  ];
  return doctorFields.some((value) => value != null && String(value).trim() !== '');
}

function getEnabledViews(state) {
  const role = state?.currentUser?.role || '';
  const views = [];
  if (hasAssignedDoctorCode(state?.currentUser)) views.push(VIEWS.kiosk);
  // Piano Turni subito dopo Home (o primo bottone se non c'è Home), prima di Calendario.
  views.push(VIEWS.turni);
  if (role === 'admin' || role === 'organizer' || role === 'director') views.push(VIEWS.calendario);
  if (role === 'admin' || role === 'director') views.push(VIEWS.medici);
  views.push(VIEWS.assenze, VIEWS.carichi);
  views.push(VIEWS.bacheca);
  if (canManageUsers()) views.push(VIEWS.utenti);
  views.push(VIEWS.manuale);
  return views;
}

function getDefaultViewForState(state) {
  if (hasAssignedDoctorCode(state?.currentUser)) return VIEWS.kiosk;
  return VIEWS.turni;
}

function renderBoardBell(unreadCount, breakdown = null) {
  const bellBtn = document.getElementById('btn-board-bell');
  const bellCount = document.getElementById('board-bell-count');
  if (!bellBtn || !bellCount) return;
  const n = Number(unreadCount || 0);
  const b = breakdown || boardBellState.breakdown || {};
  const detail = `Inbox (non lette): ${Number(b.inboxUnread || 0)} · Messaggi bacheca: ${Number(b.boardUnread || 0)} · Approvazioni pubblicazioni: ${Number(b.pendingPostApprovals || 0)} · Cambi turno (collega): ${Number(b.pendingPeerMine || 0)} · Cambi turno (Direttore): ${Number(b.pendingDirectorQueue || 0)} · Aggiornamenti richieste inviate: ${Number(b.requesterUpdates || 0)}`;
  if (n > 0) {
    bellBtn.classList.add('has-unread');
    bellCount.hidden = false;
    bellCount.textContent = n > 99 ? '99+' : String(n);
    bellBtn.setAttribute('title', `Notifiche non lette: ${n}. ${detail}`);
  } else {
    bellBtn.classList.remove('has-unread');
    bellCount.hidden = true;
    bellCount.textContent = '0';
    bellBtn.setAttribute('title', `Nessuna notifica non letta. ${detail}`);
  }
}

async function refreshBoardBell(force = false) {
  const now = Date.now();
  if (!force && (now - boardBellState.lastFetchMs) < 12000) {
    renderBoardBell(boardBellState.unreadCount, boardBellState.breakdown);
    return;
  }
  if (boardBellState.loading) return;
  boardBellState.loading = true;
  try {
    const state = getState() || {};
    const user = state.currentUser || {};
    const currentUserId = String(user?.userId || user?.id || '');
    const role = String(user?.role || '').trim().toLowerCase();
    const isDoctorUser = role === 'user' && Boolean(user?.doctorId || user?.doctor_id);
    const canHavePeerQueue = isDoctorUser;
    const canHaveDirectorQueue = role === 'admin' || role === 'director';
    const [feed, shiftReqs, directorReqs, moderationQueue, myShiftReqs, inboxUnreadRes] = await Promise.all([
      apiGetBoardFeed({ status: 'published', limit: 1 }),
      canHavePeerQueue ? apiListShiftChangeRequests('pending_peer') : Promise.resolve([]),
      canHaveDirectorQueue ? apiListShiftChangeRequests('pending') : Promise.resolve([]),
      canHaveDirectorQueue ? apiGetBoardModerationQueue(200) : Promise.resolve({ items: [] }),
      isDoctorUser ? apiListShiftChangeRequests() : Promise.resolve([]),
      apiNotificationsUnreadCount().catch(() => ({ count: 0 })),
    ]);
    const boardUnread = Number(feed?.unreadCount || 0);
    const pendingPeerMine = Array.isArray(shiftReqs)
      ? shiftReqs.filter((r) => String(r?.requested_user_id || '') === currentUserId).length
      : 0;
    const pendingDirectorQueue = Array.isArray(directorReqs) ? directorReqs.length : 0;
    const pendingPostApprovals = Array.isArray(moderationQueue?.items) ? moderationQueue.items.length : 0;
    const requesterUpdates = Array.isArray(myShiftReqs)
      ? myShiftReqs.filter((r) => {
        const st = String(r?.status || '').trim().toLowerCase();
        return String(r?.requester_user_id || '') === currentUserId && (st === 'pending' || st === 'rejected_by_peer');
      }).length
      : 0;
    const inboxUnread = Number(inboxUnreadRes?.count || 0);
    boardBellState.breakdown = {
      boardUnread,
      pendingPostApprovals,
      pendingPeerMine,
      pendingDirectorQueue,
      requesterUpdates,
      inboxUnread,
    };
    // Badge campanella = solo notifiche in-app non lette (user_notifications).
    boardBellState.unreadCount = inboxUnread;
    boardBellState.lastFetchMs = Date.now();
    renderBoardBell(boardBellState.unreadCount, boardBellState.breakdown);
  } catch (_) {
    // Silenzioso: la campana e' best-effort e non deve disturbare il rendering principale.
  } finally {
    boardBellState.loading = false;
  }
}

function ensureBoardBellPolling() {
  if (boardBellPollTimer) return;
  boardBellPollTimer = setInterval(() => {
    void refreshBoardBell(false);
  }, BOARD_BELL_POLL_MS);
}

/** @returns {'beta'|'alpha'|null} */
function preprodBadgeKind() {
  const status = (typeof window !== 'undefined' && window.__TURNI_API_STATUS) || {};
  const env = String(status.env || '').toLowerCase();
  const channel = String(status.channel || '').toLowerCase();
  const port = (typeof window !== 'undefined' && window.location?.port) ? window.location.port : '';
  const hostAlpha =
    typeof window !== 'undefined' && /turnialfa/i.test(String(window.location.hostname || ''));
  if (channel === 'alpha' || port === '3330' || hostAlpha) return 'alpha';
  if (channel === 'beta' || port === '3300') return 'beta';
  if (channel) return null;
  if (env === 'development') return 'beta';
  return null;
}

function isBetaUi() {
  return preprodBadgeKind() === 'beta';
}

function isPreprodUi() {
  return preprodBadgeKind() !== null;
}

async function render() {
  const app = document.getElementById('app');
  if (!app) return;

  try {
    const state = getState();
    if (!isBackendConnected()) {
    app.innerHTML = `
      <div class="app" style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 2rem;">
        <div class="card" style="max-width: 480px; text-align: center;">
          <div class="card-title" style="color: var(--danger);">Backend non disponibile</div>
          <p>Il server non risponde. Avvia il backend con:</p>
          <pre style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; text-align: left; overflow-x: auto;"><code>./start_server.sh</code></pre>
          <p style="font-size: 0.9rem; color: var(--text-secondary); margin-top: 1rem;">
            Tutti i dati sono salvati nel database. Senza backend l'app non può funzionare.
          </p>
        </div>
      </div>
    `;
    return;
  }

  const apiActive = isBackendConnected();
  
  // Se l'app shell non esiste, crealo
  if (!app.querySelector('.header')) {
    const enabledViews = getEnabledViews(state);
    app.innerHTML = `
    <div class="app">
      <header class="header">
        <div class="header-top">
          <div class="header-brand">
            <button type="button" class="nav-toggle btn btn-secondary btn-sm" id="nav-toggle" aria-expanded="false" aria-controls="main-nav" aria-label="Apri menu di navigazione">☰</button>
            <h1>
              Pianificazione Turni UOC - ATISI
              <span id="env-badge" class="env-badge ${isPreprodUi() ? '' : 'hidden'}">${preprodBadgeKind() === 'alpha' ? 'Alfa' : 'Beta'}</span>
            </h1>
          </div>
          <nav class="nav" id="main-nav" aria-label="Sezioni principali">
            ${enabledViews.map((view) => `<button type="button" class="nav-btn nav-btn--${view} ${currentView === view ? 'active' : ''}" data-view="${view}"><span class="nav-btn-icon" aria-hidden="true">${VIEW_META[view]?.icon || '•'}</span><span class="nav-btn-label">${VIEW_LABELS[view] || view}</span></button>`).join('')}
          </nav>
          <div class="header-right">
            <button type="button" class="btn btn-secondary btn-sm btn-board-bell" id="btn-board-bell" title="Centro notifiche" aria-label="Centro notifiche">
              <span aria-hidden="true">🔔</span>
              <span class="board-bell-count" id="board-bell-count" hidden>0</span>
            </button>
            <span id="user-label">${getUserDisplayLabel(state)}</span>
            <button type="button" class="btn btn-outline btn-sm" id="btn-account-settings" title="Recapito email e account">Impostazioni account</button>
            <button type="button" class="btn btn-outline btn-sm" id="btn-change-pwd" title="Cambia password">Cambia password</button>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-logout" title="Esci e richiedi di nuovo la password">Esci</button>
            <span id="app-version" title="Data e ora ultimo aggiornamento build">${getAppVersion()}</span>
          </div>
        </div>
        <div class="nav-backdrop" id="nav-backdrop" hidden aria-hidden="true"></div>
      </header>
      <main class="main" id="main-content"></main>
    </div>
  `;
    
    // Bind events only once
    app.querySelectorAll('.nav-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        currentView = btn.dataset.view;
        currentDoctorId = null;
        render();
      });
    });
    app.querySelector('#btn-account-settings')?.addEventListener('click', () => showAccountSettingsModal());
    app.querySelector('#btn-change-pwd')?.addEventListener('click', () => showChangePasswordModal());
    app.querySelector('#btn-board-bell')?.addEventListener('click', () => {
      openNotificationsPanel({
        onChanged: () => {
          void refreshBoardBell(true);
        },
      });
    });
    app.querySelector('#btn-logout')?.addEventListener('click', async () => {
      await apiLogout();
      clearAuth();
      window.location.reload();
    });
    bindHeaderNav(app);
    ensureBoardBellPolling();
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('notifications') === '1') {
        const nid = sp.get('nid') || undefined;
        queueMicrotask(() =>
          openNotificationsPanel({
            highlightId: nid,
            onChanged: () => {
              void refreshBoardBell(true);
            },
          }),
        );
      }
    } catch (_) {}
  }

  // Update active state e badge (versione è fissa al caricamento, non si aggiorna a ogni render)
  app.querySelectorAll('.nav-btn').forEach(btn => {
    if (btn.dataset.view === currentView) btn.classList.add('active');
    else btn.classList.remove('active');
  });
  
  const versionEl = app.querySelector('#app-version');
  if (versionEl && versionEl.textContent !== getAppVersion()) versionEl.textContent = getAppVersion();
  const envBadge = app.querySelector('#env-badge');
  if (envBadge) {
    const kind = preprodBadgeKind();
    const label = kind === 'alpha' ? 'Alfa' : 'Beta';
    if (envBadge.textContent !== label) envBadge.textContent = label;
    if (isPreprodUi()) envBadge.classList.remove('hidden');
    else envBadge.classList.add('hidden');
  }
  const userLabelEl = app.querySelector('#user-label');
  const userLabel = getUserDisplayLabel(getState());
  if (userLabelEl && userLabelEl.textContent !== userLabel) userLabelEl.textContent = userLabel;
  await refreshBoardBell(currentView === VIEWS.bacheca);

  const main = app.querySelector('#main-content');
  const enabledViews = getEnabledViews(state);
  if (currentView !== VIEWS.kiosk) disposeDoctorKioskView();
  if (!enabledViews.includes(currentView)) currentView = getDefaultViewForState(state);
  app.querySelector('.app')?.classList.toggle('app--kiosk', currentView === VIEWS.kiosk);

  switch (currentView) {
    case VIEWS.kiosk:
      renderDoctorKioskView(main, {
        availableViews: enabledViews,
        onNavigate: (view, opts = {}) => {
          if (!enabledViews.includes(view)) return;
          if (view === VIEWS.assenze && opts.primeNextMonthAbsences) {
            const { monthStart, monthEnd } = getNextCalendarMonthBounds(new Date());
            const { gridStart, gridEnd } = getIsoWeekGridSpanForCalendarMonth(monthStart, monthEnd);
            setAbsencesCalendarWeekRange(gridStart, getMondayOfWeek(gridEnd));
          }
          currentView = view;
          currentDoctorId = null;
          render();
        },
      });
      break;
    case VIEWS.calendario:
      renderCalendarPlanner(main);
      break;
    case VIEWS.medici:
      renderMediciView(main);
      break;
    case VIEWS.assenze:
      renderAbsencesCalendar(main);
      break;
    case VIEWS.turni: {
      const hasPlannerContent = !!main.querySelector('#weeks-container');
      const isNavigatingToTurni = previousView !== VIEWS.turni;
      if (!hasPlannerContent) {
        main.innerHTML = '<div class="card"><p style="color: var(--text-secondary);">Caricamento Piano Turni...</p></div>';
      }
      const scrollY = window.scrollY ?? document.documentElement.scrollTop;
      const scrollX = window.scrollLeft ?? document.documentElement.scrollLeft;
      main.style.minHeight = '200px';
      try {
        await renderShiftPlanner(main);
        if (hasPlannerContent && !isNavigatingToTurni) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const nowY = window.scrollY ?? document.documentElement.scrollTop;
              if (Math.abs(nowY - scrollY) > 5) window.scrollTo(scrollX, scrollY);
            });
          });
        }
      } catch (err) {
        console.error('Errore Piano Turni:', err);
        main.innerHTML = `<div class="card"><div class="card-title" style="color: var(--danger);">Errore</div><pre style="font-size: 0.8rem; overflow-x: auto;">${String(err?.message || err)}</pre></div>`;
      }
      break;
    }
    case VIEWS.carichi:
      renderWorkloadView(main);
      break;
    case VIEWS.bacheca:
      await renderBoardView(main);
      break;
    case VIEWS.utenti:
      renderUsersView(main);
      break;
    case VIEWS.manuale:
      renderManualView(main);
      break;
    default:
      currentView = VIEWS.turni;
      render();
  }
  previousView = currentView;
  bindHeaderNav(app);
  } catch (err) {
    console.error('Render error:', err);
    app.innerHTML = `<div class="card" style="margin: 2rem; max-width: 480px;"><div class="card-title" style="color: var(--danger);">Errore</div><pre style="font-size: 0.8rem; overflow-x: auto;">${String(err.message || err)}</pre></div>`;
  }
}


function renderMediciView(container) {
  const state = getState();
  if ((state.currentUser?.role || '') !== 'admin' && (state.currentUser?.role || '') !== 'director') {
    container.innerHTML = '<div class="card"><p style="color: var(--danger);">Accesso negato. La sezione Medici è disponibile solo per admin e direttori.</p></div>';
    return;
  }
  const backendOk = isBackendConnected();
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  if (currentDoctorId) {
    renderDoctorProfile(container, currentDoctorId, () => {
      currentDoctorId = null;
      render();
    });
    return;
  }

  // I medici cessati escono dall'elenco dal giorno successivo all'ultimo giorno
  // lavorato. Restano ovunque serva lo storico: Carichi, turni passati, export.
  const activeDoctors = state.doctors.filter(d => !isDoctorCeased(d));

  container.innerHTML = `
    <div class="card medici-list-card">
      <div class="card-title">Elenco Medici</div>
      ${canEditDoctors() ? '<button class="btn btn-primary" id="btn-new-doctor" style="margin-bottom: 1rem;">+ Nuovo Medico</button>' : ''}
      ${activeDoctors.length === 0 ? `
        <div class="empty-state">Nessun medico. Clicca "Nuovo Medico" per iniziare.</div>
      ` : `
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Sigla</th>
                <th>Nome</th>
                <th>Cognome</th>
                <th>Debito</th>
                <th>Esenzioni</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${activeDoctors.map(d => `
                <tr>
                  <td><strong>${d.sigla}</strong></td>
                  <td>${d.nome}</td>
                  <td>${d.cognome}</td>
                  <td>${d.debitoOrarioSettimanale}h</td>
                  <td>
                    ${d.esenzioneNotti ? 'Notti ' : ''}
                    ${d.esenzioneWeekend ? 'Weekend ' : ''}
                    ${d.esenzioneAttivitaCliniche ? 'Cliniche ' : ''}
                    ${d.specializzando ? 'Specializzando ' : ''}
                    ${d.abilitaDirezione ? 'Direzione' : ''}
                  </td>
                  <td>
                    ${canEditDoctors() ? `<button class="btn btn-secondary btn-sm btn-edit" data-id="${d.id}">Modifica</button>` : ''}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `}
    </div>
  `;

  container.querySelector('#btn-new-doctor')?.addEventListener('click', () => {
    currentDoctorId = null;
    const profileContainer = document.createElement('div');
    container.innerHTML = '';
    container.appendChild(profileContainer);
    renderDoctorProfile(profileContainer, null, () => {
      currentDoctorId = null;
      render();
    });
  });

  container.querySelectorAll('.btn-edit').forEach(btn => {
    btn.addEventListener('click', () => {
      currentDoctorId = btn.dataset.id;
      render();
    });
  });
}


function showForgotPasswordModal() {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" style="max-width: 400px;">
      <div class="modal-header">
        <h3 style="margin:0;font-size:1rem;">Recupero password</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="forgot-close-x" aria-label="Chiudi">×</button>
      </div>
      <form id="forgot-form">
        <div class="modal-body">
          <p style="font-size: 0.88rem; color: var(--text-secondary); margin: 0 0 0.75rem 0;">
            Inserisci il tuo <strong>username</strong> oppure l'<strong>email</strong> associata all'account. Se i dati sono corretti e c'è un recapito configurato, riceverai un messaggio con un link per impostare una nuova password.
          </p>
          <div class="form-group">
            <label for="forgot-identifier">Username o email</label>
            <input type="text" id="forgot-identifier" autocomplete="username" placeholder="es. mario.rossi o nome@ospedale.it">
          </div>
          <div id="forgot-error" style="color: var(--danger); font-size: 0.9rem; display: none;"></div>
          <div id="forgot-success" style="color: var(--success); font-size: 0.9rem; display: none;"></div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" id="forgot-cancel">Chiudi</button>
          <button type="submit" class="btn btn-primary" id="forgot-submit">Invia richiesta</button>
        </div>
      </form>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#forgot-cancel').addEventListener('click', close);
  overlay.querySelector('#forgot-close-x').addEventListener('click', close);
  overlay.querySelector('#forgot-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = overlay.querySelector('#forgot-error');
    const okEl = overlay.querySelector('#forgot-success');
    errEl.style.display = 'none';
    okEl.style.display = 'none';
    const id = overlay.querySelector('#forgot-identifier').value;
    overlay.querySelector('#forgot-submit').disabled = true;
    try {
      const res = await apiForgotPassword(id);
      okEl.textContent = res.message || 'Richiesta registrata.';
      okEl.style.display = 'block';
    } catch (err) {
      errEl.textContent = err?.message || String(err);
      errEl.style.display = 'block';
    } finally {
      overlay.querySelector('#forgot-submit').disabled = false;
    }
  });
  overlay.querySelector('#forgot-identifier').focus();
}

function showResetPasswordCard(app, token, onDone) {
  document.body.style.backgroundImage = 'url("/assets/login-bg.png")';
  document.body.style.backgroundSize = 'cover';
  document.body.style.backgroundPosition = 'center';
  document.body.style.backgroundRepeat = 'no-repeat';
  app.innerHTML = `
    <div class="app login-page" style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 1rem; box-sizing: border-box;">
      <div class="card login-card" style="max-width: min(380px, calc(100vw - 2rem)); width: 100%; box-sizing: border-box;">
        <div class="login-page-brand">
          <div class="login-page-title">Nuova password</div>
          ${isPreprodUi() ? `<span class="env-badge env-badge--login" title="${preprodBadgeKind() === 'alpha' ? 'Ambiente alfa' : 'Ambiente beta / pre-produzione'}">${preprodBadgeKind() === 'alpha' ? 'Alfa' : 'Beta'}</span>` : ''}
        </div>
        <p style="font-size: 0.88rem; color: var(--text-secondary); margin: 0 0 0.75rem 0;">Scegli una nuova password per il tuo account (minimo 4 caratteri).</p>
        <form id="reset-pwd-form" style="display: flex; flex-direction: column; gap: 0.5rem;">
          <div class="form-group" style="margin-bottom: 0;">
            <label for="reset-new">Nuova password</label>
            <input type="password" id="reset-new" autocomplete="new-password" minlength="4" required style="width: 100%; padding: 0.4rem;">
          </div>
          <div class="form-group" style="margin-bottom: 0;">
            <label for="reset-confirm">Conferma password</label>
            <input type="password" id="reset-confirm" autocomplete="new-password" minlength="4" required style="width: 100%; padding: 0.4rem;">
          </div>
          <div id="reset-error" style="color: var(--danger); font-size: 0.9rem; display: none;"></div>
          <button type="submit" class="btn btn-primary" id="reset-submit">Salva e accedi al login</button>
        </form>
      </div>
    </div>
  `;
  const form = app.querySelector('#reset-pwd-form');
  const errEl = app.querySelector('#reset-error');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.style.display = 'none';
    const a = app.querySelector('#reset-new').value;
    const b = app.querySelector('#reset-confirm').value;
    if (a !== b) {
      errEl.textContent = 'Le password non coincidono.';
      errEl.style.display = 'block';
      return;
    }
    if ((a || '').length < 4) {
      errEl.textContent = 'Almeno 4 caratteri.';
      errEl.style.display = 'block';
      return;
    }
    app.querySelector('#reset-submit').disabled = true;
    try {
      await apiResetPassword(token, a);
      onDone();
    } catch (err) {
      errEl.textContent = err?.message || String(err);
      errEl.style.display = 'block';
    } finally {
      app.querySelector('#reset-submit').disabled = false;
    }
  });
  app.querySelector('#reset-new').focus();
}

function showAccountSettingsModal() {
  closeHeaderNavIfOpen();
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" style="max-width: 420px;">
      <div class="modal-header">
        <h3 style="margin:0;font-size:1rem;">Impostazioni account</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="acct-close-x" aria-label="Chiudi">×</button>
      </div>
      <form id="acct-form">
        <div class="modal-body">
          <p style="font-size: 0.88rem; color: var(--text-secondary); margin: 0 0 0.75rem 0;">
            Indica i recapiti email su cui ricevere piano turni e comunicazioni. Lascia vuoto per rimuoverli.
          </p>
          <div class="form-group">
            <div id="acct-emails-editor"></div>
          </div>
          <div id="acct-error" style="color: var(--danger); font-size: 0.9rem; display: none;"></div>
          <div id="acct-ok" style="color: var(--success); font-size: 0.9rem; display: none;">Salvato.</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" id="acct-cancel">Chiudi</button>
          <button type="submit" class="btn btn-primary" id="acct-submit">Salva</button>
        </div>
      </form>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#acct-cancel').addEventListener('click', close);
  overlay.querySelector('#acct-close-x').addEventListener('click', close);
  const editorHost = overlay.querySelector('#acct-emails-editor');
  renderEmailRecipientsEditor(editorHost, []);
  apiGetMe().then((me) => {
    const list = Array.isArray(me?.emails) && me.emails.length
      ? me.emails
      : (me?.email ? [{ email: me.email, isPrimary: true, recvShiftPdf: true, recvBoard: true }] : []);
    renderEmailRecipientsEditor(editorHost, list);
  }).catch(() => {});
  overlay.querySelector('#acct-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = overlay.querySelector('#acct-error');
    const okEl = overlay.querySelector('#acct-ok');
    errEl.style.display = 'none';
    okEl.style.display = 'none';
    const emails = collectEmailRecipients(editorHost);
    const emailsError = validateEmailRecipients(emails);
    if (emailsError) {
      errEl.textContent = emailsError;
      errEl.style.display = 'block';
      return;
    }
    overlay.querySelector('#acct-submit').disabled = true;
    try {
      await apiPatchMe({ emails });
      okEl.style.display = 'block';
      const primary = emails.find((e) => e.isPrimary)?.email || null;
      updateStoredUser((u) => ({ ...u, email: primary }));
      setTimeout(close, 600);
    } catch (err) {
      errEl.textContent = err?.message || String(err);
      errEl.style.display = 'block';
    } finally {
      overlay.querySelector('#acct-submit').disabled = false;
    }
  });
}

function showLoginPage(app, onSuccess) {
  document.body.style.backgroundImage = 'url("/assets/login-bg.png")';
  document.body.style.backgroundSize = 'cover';
  document.body.style.backgroundPosition = 'center';
  document.body.style.backgroundRepeat = 'no-repeat';
  app.innerHTML = `
    <div class="app login-page" style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 1rem; box-sizing: border-box;">
      <div class="card login-card" style="max-width: min(340px, calc(100vw - 2rem)); width: 100%; box-sizing: border-box;">
        <div class="login-page-brand">
          <div class="login-page-title">Pianificazione Turni UOC - ATISI</div>
          ${isPreprodUi() ? `<span class="env-badge env-badge--login" title="${preprodBadgeKind() === 'alpha' ? 'Ambiente alfa' : 'Ambiente beta / pre-produzione'}">${preprodBadgeKind() === 'alpha' ? 'Alfa' : 'Beta'}</span>` : ''}
        </div>
        <form id="login-form" style="display: flex; flex-direction: column; gap: 0.4rem;">
          <div class="form-group" style="margin-bottom: 0;">
            <label for="login-username">Username</label>
            <input type="text" id="login-username" autocomplete="username" placeholder="Username" style="width: 100%; padding: 0.4rem;">
          </div>
          <div class="form-group" style="margin-bottom: 0;">
            <label for="login-password">Password</label>
            <input type="password" id="login-password" autocomplete="current-password" placeholder="Password" style="width: 100%; padding: 0.4rem;">
          </div>
          <label for="login-remember" class="login-remember" style="display: flex; align-items: flex-start; gap: 0.45rem; font-size: 0.88rem; cursor: pointer; margin-top: 0.15rem;">
            <input type="checkbox" id="login-remember" style="margin: 0.15rem 0 0 0; flex-shrink: 0;">
            <span>Ricorda questo dispositivo<br><span style="color: var(--text-secondary); font-size: 0.8rem;">Non serve la password per 30 giorni. Lascia vuoto su un dispositivo condiviso.</span></span>
          </label>
          <div id="login-error" style="color: var(--danger); font-size: 0.9rem; display: none;"></div>
          <button type="submit" class="btn btn-primary" id="login-submit">Accedi</button>
          <p style="margin: 0.25rem 0 0 0; text-align: center; font-size: 0.88rem;">
            <button type="button" id="login-forgot" style="padding: 0; font-size: inherit; background: none; border: none; color: var(--accent); text-decoration: underline; cursor: pointer;">Password dimenticata?</button>
          </p>
        </form>
      </div>
    </div>
  `;
  app.querySelector('#login-forgot')?.addEventListener('click', () => showForgotPasswordModal());
  const form = app.querySelector('#login-form');
  const userInput = app.querySelector('#login-username');
  const pwInput = app.querySelector('#login-password');
  const rememberInput = app.querySelector('#login-remember');
  const errEl = app.querySelector('#login-error');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.style.display = 'none';
    errEl.textContent = '';
    const username = (userInput.value || '').trim();
    if (!username) {
      errEl.textContent = 'Inserisci username e password.';
      errEl.style.display = 'block';
      userInput.focus();
      return;
    }
    const submitBtn = app.querySelector('#login-submit');
    submitBtn.disabled = true;
    try {
      const remember = Boolean(rememberInput?.checked);
      const result = await login(username, pwInput.value, remember);
      if (result) {
        setAuth(result.token, result.user, remember);
        onSuccess();
        document.body.style.backgroundImage = '';
        document.body.style.backgroundSize = '';
        document.body.style.backgroundPosition = '';
        document.body.style.backgroundRepeat = '';
      } else {
        errEl.textContent = 'Credenziali non valide.';
        errEl.style.display = 'block';
        pwInput.focus();
      }
    } catch (err) {
      errEl.textContent = err?.message || 'Errore di connessione.';
      errEl.style.display = 'block';
      pwInput.focus();
    } finally {
      submitBtn.disabled = false;
    }
  });
  userInput.focus();
}

function showChangePasswordModal() {
  closeHeaderNavIfOpen();
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'change-pwd-title');
  const inpStyle =
    'width:100%;box-sizing:border-box;min-height:3rem;font-size:1rem;padding:0.7rem 0.85rem;' +
    'background:var(--bg-tertiary);border:1px solid var(--border);border-radius:var(--radius);color:var(--text-primary);';
  overlay.innerHTML = `
    <div class="modal change-password-modal" style="max-width: min(440px, calc(100vw - 2rem)); position: relative; z-index: 1;">
      <div class="modal-header">
        <h3 id="change-pwd-title" style="margin:0;font-size:1.05rem;">Cambia password</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="pwd-close-x" aria-label="Chiudi">×</button>
      </div>
      <form id="change-pwd-form" novalidate>
        <div class="modal-body">
          <div class="form-group">
            <label for="pwd-current">Password attuale</label>
            <input type="password" id="pwd-current" name="pwd-current" autocomplete="current-password" inputmode="text" style="${inpStyle}">
          </div>
          <div class="form-group">
            <label for="pwd-new">Nuova password</label>
            <input type="password" id="pwd-new" name="pwd-new" minlength="4" autocomplete="new-password" placeholder="Almeno 4 caratteri" style="${inpStyle}">
          </div>
          <div class="form-group">
            <label for="pwd-confirm">Conferma nuova password</label>
            <input type="password" id="pwd-confirm" name="pwd-confirm" minlength="4" autocomplete="new-password" style="${inpStyle}">
          </div>
          <div id="pwd-error" style="color: var(--danger); font-size: 0.9rem; display: none;"></div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" id="pwd-cancel">Annulla</button>
          <button type="submit" class="btn btn-primary" id="pwd-submit">Salva</button>
        </div>
      </form>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => {
    overlay.remove();
  };

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('.change-password-modal')?.addEventListener('click', (e) => e.stopPropagation());
  overlay.querySelector('#pwd-cancel').addEventListener('click', close);
  overlay.querySelector('#pwd-close-x')?.addEventListener('click', close);

  overlay.querySelector('#change-pwd-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const current = (overlay.querySelector('#pwd-current')?.value || '').trim();
    const newPwd = (overlay.querySelector('#pwd-new')?.value || '').trim();
    const confirm = (overlay.querySelector('#pwd-confirm')?.value || '').trim();
    const errEl = overlay.querySelector('#pwd-error');
    if (!current) {
      errEl.textContent = 'Inserisci la password attuale.';
      errEl.style.display = 'block';
      overlay.querySelector('#pwd-current')?.focus();
      return;
    }
    if (newPwd !== confirm) {
      errEl.textContent = 'La conferma non coincide con la nuova password.';
      errEl.style.display = 'block';
      return;
    }
    if (newPwd.length < 4) {
      errEl.textContent = 'La nuova password deve avere almeno 4 caratteri.';
      errEl.style.display = 'block';
      return;
    }
    errEl.style.display = 'none';
    overlay.querySelector('#pwd-submit').disabled = true;
    try {
      await apiChangeMyPassword(current, newPwd);
      close();
      alert('Password aggiornata. La prossima volta che accedi userai la nuova password.');
    } catch (err) {
      errEl.textContent = err?.message || err;
      errEl.style.display = 'block';
    } finally {
      overlay.querySelector('#pwd-submit').disabled = false;
    }
  });

  requestAnimationFrame(() => {
    overlay.querySelector('#pwd-current')?.focus();
  });
}

// Init
(async () => {
  initLayoutHints();
  const app = document.getElementById('app');
  if (!app) return;
  mountNetworkStatusBanner();
  let apiStatus = null;
  try {
    apiStatus = await getApiStatus();
  } catch (_) {
    apiStatus = null;
  }
  window.__TURNI_API_STATUS = apiStatus;
  applyBuildMetadataFromStatus(apiStatus);
  applySolverTimeoutFromStatus(apiStatus);
  void registerServiceWorker();
  const isAuthError = (err) => {
    const msg = String(err?.message || err || '');
    return /API 401|Token mancante|Token non valido|Sessione non valida|Sessione revocata|Sessione scaduta/i.test(msg);
  };
  const startAuthenticatedUi = async () => {
    app.textContent = 'Caricamento...';
    try {
      await initStore();
    } catch (err) {
      if (isAuthError(err)) {
        clearAuth();
        showLoginPage(app, startAuthenticatedUi);
        return;
      }
      throw err;
    }
    currentView = getDefaultViewForState(getState());
    await render();
    subscribe(() => { try { render().catch(e => { if (window.__showError) window.__showError(e.message || e); }); } catch (e) { if (window.__showError) window.__showError(e.message || e); } });
    void syncWebPushSubscription().catch(() => {});
  };
  try {
    app.textContent = 'Connessione al backend...';
    const backendOk = apiStatus != null;
    if (!backendOk) {
      app.innerHTML = `
        <div class="app" style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 2rem;">
          <div class="card" style="max-width: 480px; text-align: center;">
            <div class="card-title" style="color: var(--danger);">Backend non disponibile</div>
            <p>Il server non risponde. Avvia il backend con:</p>
            <pre style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; text-align: left; overflow-x: auto;"><code>./start_server.sh</code></pre>
          </div>
        </div>
      `;
      return;
    }
    if (!getAuthToken()) {
      const params = new URLSearchParams(window.location.search);
      const resetTok = params.get('reset');
      if (resetTok) {
        showResetPasswordCard(app, resetTok, () => {
          try {
            const u = new URL(window.location.href);
            u.searchParams.delete('reset');
            window.history.replaceState({}, '', u.pathname + (u.search ? u.search : '') + u.hash);
          } catch (_) {}
          alert('Password aggiornata. Accedi con la nuova password.');
          showLoginPage(app, startAuthenticatedUi);
        });
      } else {
        showLoginPage(app, startAuthenticatedUi);
      }
      return;
    }
    await startAuthenticatedUi();
  } catch (err) {
    const msg = err?.message || err?.stack || String(err);
    if (window.__showError) window.__showError(msg);
    else {
      app.innerHTML = '<div style="padding: 2rem; font-family: sans-serif;"><p style="color: #c00;">Errore: ' + String(msg).replace(/</g, '&lt;') + '</p></div>';
    }
  }
})();

